/**
 * URL builder — single source of truth for public artifact URLs.
 *
 * Every consumer (pages, components, emails, sitemap, SEO push, notifications)
 * passes a row `{id, slug}` and gets back a path. Inline template literals
 * like `/niche/${id}` are banned — use `urls.niche(row)` so a future URL
 * shape change (e.g. adding category prefix) is one-file.
 *
 * Two callsite shapes:
 *   urls.niche(nicheRow)                          → "/niche/<slug>"
 *   urls.niche(nicheRow, { absolute: true })      → "https://foundrystart.com/niche/<slug>"
 *   urls.byType("plan", planRow)                  → "/plan/<slug>"   (used by emails / seo notify)
 */
import { BRAND } from "./brand";
import type { ArtifactType } from "./seo/notify";

interface UrlOpts {
  absolute?: boolean;
}

interface ArtifactRow {
  id: string;
  slug: string;
}

function withBase(path: string, opts?: UrlOpts): string {
  return opts?.absolute ? `${BRAND.url}${path}` : path;
}

export const urls = {
  niche: (n: ArtifactRow, opts?: UrlOpts) => withBase(`/niche/${n.slug}`, opts),

  nicheCandidate: (n: ArtifactRow, candidateId: string, opts?: UrlOpts) =>
    withBase(`/niche/${n.slug}/c/${encodeURIComponent(candidateId)}`, opts),

  plan: (p: ArtifactRow, opts?: UrlOpts) => withBase(`/plan/${p.slug}`, opts),

  brand: (b: ArtifactRow, opts?: UrlOpts) => withBase(`/brand/${b.slug}`, opts),

  byType: (type: ArtifactType, payload: ArtifactRow, opts?: UrlOpts) =>
    urls[type](payload, opts),
};
